// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

// Создаем контекст
const AuthContext = createContext();

// Провайдер для контекста аутентификации
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Проверяем аутентификацию пользователя при загрузке приложения
    useEffect(() => {
        const checkAuth = async () => {
            try {
                // Проверяем текущий путь, чтобы избежать циклических запросов
                const currentPath = window.location.pathname;
                if (currentPath === '/login' || currentPath === '/register') {
                    // На страницах логина/регистрации просто завершаем загрузку
                    setLoading(false);
                    return;
                }

                // Получаем информацию о пользователе
                const response = await api.get('/api/auth/me');
                setUser(response.data.data);
            } catch (err) {
                console.error('Auth check error:', err);
                // Если запрос не удался, пользователь не аутентифицирован
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    // Регистрация пользователя
    const register = async (username, password, referralCode = null) => {
        try {
            setError('');

            // Создаем объект с данными регистрации
            const registrationData = { username, password };

            // Добавляем реферальный код, если он есть
            if (referralCode) {
                registrationData.referralCode = referralCode;
            }

            const response = await api.post('/api/auth/register', registrationData);

            // Сохраняем данные пользователя
            setUser(response.data.data);

            return { success: true };
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Ошибка при регистрации. Попробуйте еще раз.'
            );
            return { success: false, message: err.response?.data?.message };
        }
    };

    // Вход пользователя
    const login = async (username, password) => {
        try {
            setError('');
            const response = await api.post('/api/auth/login', { username, password });

            // Токен уже установлен в cookie, просто сохраняем пользователя
            setUser(response.data.data);

            return { success: true };
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Ошибка при входе. Проверьте имя пользователя и пароль.'
            );
            return { success: false, message: err.response?.data?.message };
        }
    };

    // Выход пользователя
    const logout = async () => {
        try {
            // Отправляем запрос на выход
            await api.post('/api/auth/logout');
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            // Сбрасываем состояние пользователя
            setUser(null);
        }
    };

    // Очистка ошибки
    const clearError = () => {
        setError('');
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                error,
                register,
                login,
                logout,
                clearError
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Хук для использования контекста аутентификации
export const useAuth = () => {
    return useContext(AuthContext);
};